import { AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useLanguage } from '@/contexts/LanguageContext';
import { groupFailures, type FailedTable } from '@/lib/sourceTableBatch';

/**
 * Props for the TableBatchFailureAlert component.
 */
interface TableBatchFailureAlertProps {
  /** Number of tables added successfully in the batch. */
  addedCount: number;
  /** Total number of tables submitted in the batch. */
  totalCount: number;
  /** Tables that could not be added, with their translated reason. */
  failures: readonly FailedTable[];
}

/**
 * Summarizes a partially failed add-source-tables batch: how many were added, and the failed
 * tables grouped by reason (with long lists collapsed to a count).
 */
export function TableBatchFailureAlert({ addedCount, totalCount, failures }: TableBatchFailureAlertProps) {
  const { t } = useLanguage();
  const strings = t.sources.tableBatch;

  return (
    <Alert className="background-error border-error">
      <AlertCircle className="h-4 w-4 text-error" />
      <AlertDescription className="text-error">
        <p>
          {strings.partialFailure
            .replace('{added}', String(addedCount))
            .replace('{total}', String(totalCount))
            .replace('{failed}', String(failures.length))}
        </p>
        <ul className="mt-1 list-disc pl-5">
          {groupFailures(failures).map((group) => (
            <li key={group.message}>
              {group.message}: {group.names.join(', ')}
              {group.hiddenCount > 0 && ` ${strings.moreTables.replace('{count}', String(group.hiddenCount))}`}
            </li>
          ))}
        </ul>
      </AlertDescription>
    </Alert>
  );
}
